import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/** Matches 3-decimal tCO₂ display: non-zero magnitudes below this use a bounded hint. */
const CBAM_TCO2_DISPLAY_THRESHOLD = 0.001

export default function useI18n() {
  const {
    t,
    i18n: { changeLanguage, language: currentLanguage }
  } = useTranslation()

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: 4
      }).format,
    [currentLanguage]
  )

  const numberFormatter2Decimals = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: 2
      }).format,
    [currentLanguage]
  )

  const numberFormatter3Decimals = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: 3
      }).format,
    [currentLanguage]
  )

  const numberFormatter6Decimals = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: 6
      }).format,
    [currentLanguage]
  )

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(currentLanguage, {
        month: 'long',
        year: 'numeric',
        day: 'numeric'
      }).format,
    [currentLanguage]
  )

  const dateLongFormatterMonthYear = useMemo(
    () =>
      new Intl.DateTimeFormat(currentLanguage, {
        month: 'short',
        year: '2-digit'
      }).format,
    [currentLanguage]
  )

  const shortDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(currentLanguage, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      }).format,
    [currentLanguage]
  )
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
      }).format,
    [currentLanguage]
  )

  const currencyFormatter2Decimals = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2
      }).format,
    [currentLanguage]
  )

  const currencyFormatterWith3Decimal = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 3
      }).format,
    [currentLanguage]
  )

  const numberFormatterNoDecimals = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        maximumFractionDigits: 0
      }).format,
    [currentLanguage]
  )

  const formatCbamTco2Display = useMemo(
    () => (value: number | undefined | null) => {
      if (value === undefined || value === null) {
        return '-'
      }
      const abs = Math.abs(value)
      if (abs > 0 && abs < CBAM_TCO2_DISPLAY_THRESHOLD) {
        return value > 0
          ? t('cbamReporting:cbamReportDetails.emissionsDisplay.lessThanTco2', {
              threshold: numberFormatter3Decimals(CBAM_TCO2_DISPLAY_THRESHOLD)
            })
          : t('cbamReporting:cbamReportDetails.emissionsDisplay.greaterThanTco2', {
              threshold: numberFormatter3Decimals(-CBAM_TCO2_DISPLAY_THRESHOLD)
            })
      }
      return `${numberFormatter3Decimals(value)} tCO₂`
    },
    [t, numberFormatter3Decimals]
  )

  return {
    t,
    currentLanguage,
    handleChangeLanguage: changeLanguage,
    numberFormatter,
    numberFormatter2Decimals,
    dateFormatter,
    currencyFormatter,
    currencyFormatter2Decimals,
    currencyFormatterWith3Decimal,
    shortDateFormatter,
    numberFormatterNoDecimals,
    dateLongFormatterMonthYear,
    numberFormatter3Decimals,
    numberFormatter6Decimals,
    formatCbamTco2Display
  }
}
